import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Card,
  Metric,
  Text,
  Title,
  AreaChart,
  BarChart,
  LineChart,
  BarList,
  Select,
  SelectItem,
  Button,
} from "@tremor/react";
import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";
import VentasPorProductoSection from "./VentasPorProductoSection";
import HeatmapFranjaHoraria from "./HeatmapFranjaHoraria";
import DashboardSidebar from "./DashboardSidebar";
import DashboardHeader from "./DashboardHeader";
import ThickDonut from "./ThickDonut";
import PctBars from "./PctBars";
import { useFilterContext } from "../context/FilterContext";
import { PALETTE, CANAL_COLORS, coloresPara } from "../lib/palette";
import { apiFetch } from "../api/client";

/** Monto corto tipo S/ 2.41M / S/ 0.26M para la leyenda del mix por canal. */
function formatCompactCurrency(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `S/ ${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `S/ ${(n / 1_000).toFixed(0)}k`;
  return `S/ ${n.toFixed(0)}`;
}

const MESES_CORTO = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

interface InsightsResponse {
  restaurant_id: string;
  restaurant_name: string;
  start_date: string;
  end_date: string;
  ticket_prom_comensal: number | null;
  top_zones: Array<{
    zone: string;
    total_sales: number;
    rotation_count: number;
    avg_ticket: number;
  }>;
  mermas: {
    mermas_amount: number;
    ventas_amount: number;
    pct: number;
  } | null;
}

interface ResumenResponse {
  restaurant_id: string;
  mes_actual: string;
  ventas_actual: number | null;
  ventas_anio_anterior: number | null;
  var_anio_anterior_pct: number | null;
  ventas_periodo_previo: number | null;
  var_periodo_previo_pct: number | null;
  pedidos_total: number | null;
  pedidos_promedio: number | null;
  pax_total: number | null;
  pax_promedio: number | null;
  pedidos_total_prev: number | null;
  var_pedidos_total_pct: number | null;
  pedidos_promedio_prev: number | null;
  var_pedidos_promedio_pct: number | null;
  pax_total_prev: number | null;
  var_pax_total_pct: number | null;
  pax_promedio_prev: number | null;
  var_pax_promedio_pct: number | null;
  n_dias_diario: number;
  por_dia_semana: Array<{
    dia: string;
    avg_pedidos: number;
    avg_pax: number;
    avg_venta: number;
    n_dias: number;
  }>;
  mix_canal: Array<{ canal: string; venta: number }>;
  ventas_diarias: Array<{ dd: number; venta: number }>;
}

interface VentasMensualesResponse {
  restaurant_id: string;
  meses: Array<{
    anio: number;
    mm: number;
    label: string;
    venta_total: number;
    canales: Array<{ canal: string; venta: number }>;
  }>;
}

interface VentasDiariasCmpResponse {
  restaurant_id: string;
  anio_t: number;
  mm_t: number;
  label_t: string;
  anio_t_menos_1: number;
  mm_t_menos_1: number;
  label_t_menos_1: string;
  dias: Array<{ dd: number; venta_t: number | null; venta_t_menos_1: number | null; venta_anio_anterior: number | null }>;
}

interface HeatmapResponse {
  restaurant_id: string;
  demo: boolean;
  celdas: Array<{ dia_semana: string; franja_horaria: string; ventas: number | null }>;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);

const formatNumber = (n: number) => new Intl.NumberFormat("es-PE").format(n);

const formatDateISO = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Delta para valores YA expresados en porcentaje (ej. 7.43 = 7.43%). */
function DeltaPct({ value }: { value: number | null | undefined }) {
  if (value == null || value === 0) {
    return <span className="text-tremor-content">—</span>;
  }
  const positive = value > 0;
  const arrow = positive ? "▲" : "▼";
  const cls = positive ? "text-emerald-600" : "text-red-600";
  return <span className={`font-semibold ${cls}`}>{arrow} {Math.abs(value).toFixed(1)}%</span>;
}

export default function CommercialDashboard() {
  const {
    restaurants, meses, selectedRestaurantId, selectedRestaurantSlug,
    mesSel, sidebarOpen, error, setSelectedRestaurant, setMesSel, toggleSidebar,
  } = useFilterContext();

  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [resumen, setResumen] = useState<ResumenResponse | null>(null);
  const [ventasMensuales, setVentasMensuales] = useState<VentasMensualesResponse | null>(null);
  const [ventasDiariasCmp, setVentasDiariasCmp] = useState<VentasDiariasCmpResponse | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null);
  // YTD de ventas por producto (para el KPI "Venta acum." junto a % Mermas).
  const [ventaYtd, setVentaYtd] = useState<number | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const restaurantsLoading = restaurants.length === 0 && !error;

  const dateRange = useMemo(() => {
    if (!mesSel) return null;
    return {
      from: new Date(mesSel.anio, mesSel.mm - 1, 1),
      to: new Date(mesSel.anio, mesSel.mm, 0),
    };
  }, [mesSel]);

  const restaurantName = useMemo(
    () => restaurants.find((r) => r.id === selectedRestaurantId)?.name ?? "",
    [restaurants, selectedRestaurantId]
  );

  const selectedRestaurant = useMemo(
    () => restaurants.find((r) => r.id === selectedRestaurantId) ?? null,
    [restaurants, selectedRestaurantId]
  );

  // ── Fetch: insights del periodo actual ──
  useEffect(() => {
    if (!selectedRestaurantSlug) return;

    let cancelled = false;
    async function fetchInsights() {
      try {
        const params = new URLSearchParams({ restaurant_id: selectedRestaurantSlug });
        if (dateRange?.from) {
          params.set("start_date", formatDateISO(dateRange.from));
        }
        if (dateRange?.to) {
          params.set("end_date", formatDateISO(dateRange.to));
        }
        const res = await apiFetch(`/api/comercial/insights/?${params.toString()}`);
        if (!res.ok) {
          if (!cancelled) setInsights(null);
          return;
        }
        const body: InsightsResponse = await res.json();
        if (!cancelled) setInsights(body);
      } catch (err) {
        console.warn("Error fetching insights:", err);
        if (!cancelled) setInsights(null);
      }
    }
    fetchInsights();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, dateRange]);

  // ── Fetch: YTD de ventas por producto (KPI "Venta acum." junto a % Mermas) ──
  useEffect(() => {
    if (!selectedRestaurantSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ restaurant_id: selectedRestaurantSlug, limit: "1" });
        if (mesSel) {
          params.set("anio", String(mesSel.anio));
          params.set("mm", String(mesSel.mm));
        }
        const res = await apiFetch(`/api/comercial/ventas-por-producto?${params.toString()}`);
        if (!res.ok) {
          if (!cancelled) setVentaYtd(null);
          return;
        }
        const body = await res.json();
        if (!cancelled) setVentaYtd(typeof body.venta_ytd === "number" ? body.venta_ytd : null);
      } catch {
        if (!cancelled) setVentaYtd(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);


  // ── Fetch: resumen consolidado (ventas comparativo + pedidos/pax diario) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/comercial/resumen?${params}`);
        if (!res.ok) {
          if (!cancelled) setResumen(null);
          return;
        }
        const body: ResumenResponse = await res.json();
        if (!cancelled) setResumen(body);
      } catch (err) {
        console.warn("Error fetching resumen:", err);
        if (!cancelled) setResumen(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: ventas por mes y canal (últimos 7 meses) ──
  useEffect(() => {
    if (!selectedRestaurantSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/comercial/ventas-mensuales?restaurant_id=${selectedRestaurantSlug}`);
        if (!res.ok) {
          if (!cancelled) setVentasMensuales(null);
          return;
        }
        const body: VentasMensualesResponse = await res.json();
        if (!cancelled) setVentasMensuales(body);
      } catch (err) {
        console.warn("Error fetching ventas-mensuales:", err);
        if (!cancelled) setVentasMensuales(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug]);

  // ── Fetch: ventas diarias comparativas (mes t vs mes t-1) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/comercial/ventas-diarias-comparativas?${params}`);
        if (!res.ok) {
          if (!cancelled) setVentasDiariasCmp(null);
          return;
        }
        const body: VentasDiariasCmpResponse = await res.json();
        if (!cancelled) setVentasDiariasCmp(body);
      } catch (err) {
        console.warn("Error fetching ventas-diarias-comparativas:", err);
        if (!cancelled) setVentasDiariasCmp(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: heatmap Ventas en salón por franja horaria (oper_ocupabilidad_mesas) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm_fin: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/comercial/ventas-franja-horaria?${params}`);
        if (!res.ok) {
          if (!cancelled) setHeatmap(null);
          return;
        }
        const body: HeatmapResponse = await res.json();
        if (!cancelled) setHeatmap(body);
      } catch (err) {
        console.warn("Error fetching ventas-franja-horaria:", err);
        if (!cancelled) setHeatmap(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Serie diaria de ventas (comercial_diario_ventas, vía resumen) ──
  const ventasDiariasData = useMemo(
    () =>
      (resumen?.ventas_diarias ?? []).map((d) => ({
        date: String(d.dd).padStart(2, "0"),
        ventas: d.venta,
      })),
    [resumen]
  );

  // ── Ventas diarias: mes t vs mes anterior (t-1) ──
  // Los días sin dato se representan como 0 (no como hueco vacío).
  const ventasDiariasMesAntData = useMemo(() => {
    return (ventasDiariasCmp?.dias ?? []).map((d) => ({
      dd: d.dd,
      [ventasDiariasCmp?.label_t ?? "Mes t"]: d.venta_t ?? 0,
      [ventasDiariasCmp?.label_t_menos_1 ?? "Mes t-1"]: d.venta_t_menos_1 ?? 0,
    }));
  }, [ventasDiariasCmp]);

  // ── Comparativo diario año anterior: mes t vs año anterior ──
  const ventasDiariasCmpData = useMemo(() => {
    return (ventasDiariasCmp?.dias ?? []).map((d) => ({
      dd: d.dd,
      [ventasDiariasCmp?.label_t ?? "Mes t"]: d.venta_t ?? 0,
      "Año anterior": d.venta_anio_anterior ?? 0,
    }));
  }, [ventasDiariasCmp]);

  // ── Días de la semana: rótulos de 3 letras + % de participación ──
  const diasSemanaData = useMemo(() => {
    const rows = resumen?.por_dia_semana ?? [];
    const total = rows.reduce((s, d) => s + (d.avg_venta || 0), 0);
    const map3: Record<string, string> = {
      Lunes: "lun", Martes: "mar", Miercoles: "mie", Jueves: "jue",
      Viernes: "vie", Sabado: "sab", Domingo: "dom",
    };
    return rows.map((d) => ({
      label: map3[d.dia] ?? d.dia.slice(0, 3).toLowerCase(),
      fullLabel: d.dia,
      value: d.avg_venta,
      pct: total > 0 ? (d.avg_venta / total) * 100 : 0,
    }));
  }, [resumen]);

  // ── Ventas por mes y canal → datos para BarChart apilado ──
  const ventasMensualesData = useMemo(() => {
    return (ventasMensuales?.meses ?? []).map((m) => {
      // Etiqueta del eje X: solo las 3 primeras letras del mes, sin año.
      const mesCorto = (m.label.split(" ")[0] ?? m.label).slice(0, 3);
      const row: Record<string, number | string> = { mes: mesCorto };
      for (const c of m.canales) {
        row[c.canal] = c.venta;
      }
      return row;
    });
  }, [ventasMensuales]);

  // Canales con datos (para no mostrar segmentos vacíos en el gráfico apilado
  // ni en el dropdown). Deriva del backend: incluye Panguch/Rappi para Cosme.
  const canalesConDatos = useMemo(() => {
    const totales = new Map<string, number>();
    for (const m of ventasMensuales?.meses ?? []) {
      for (const c of m.canales) {
        totales.set(c.canal, (totales.get(c.canal) ?? 0) + c.venta);
      }
    }
    return Array.from(totales.entries())
      .filter(([, v]) => v > 0)
      .map(([canal]) => canal);
  }, [ventasMensuales]);

  const canalesVisibles = useMemo(
    () => (channelFilter === "all" ? canalesConDatos : [channelFilter]),
    [channelFilter, canalesConDatos]
  );

  // ── Export PNG/PDF: captura el dashboard (excluyendo el sidebar) ──
  const handleExport = useCallback(async (format: "png" | "pdf") => {
    if (!exportRef.current || exporting) return;
    setExporting(true);
    try {
      // Detectar si el dashboard está en dark mode para que el export respete el tema
      const isDark = document.documentElement.classList.contains("dark");
      // Captura solo el contenedor del dashboard (no el sidebar ni header global)
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: isDark ? "#0b1220" : "#ffffff",
        scale: 2, // retina-quality
        logging: false,
        useCORS: true,
      });
      const filename = `comercial-${restaurantName || "dashboard"}-${formatDateISO(new Date())}`;

      if (format === "png") {
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${filename}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, "image/png");
      } else {
        // PDF: usar jsPDF con orientación landscape
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${filename}.pdf`);
      }
    } catch (err) {
      console.error("Error al exportar:", err);
      alert(`Error al exportar: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  }, [exporting, restaurantName]);

  // F5: helper para distinguir entre "sin dato" (null) y "valor real es 0"
  function renderKpi(value: number | null | undefined, formatter: (n: number) => string) {
    if (value === null || value === undefined) {
      return (
        <span
          className="text-tremor-content text-sm italic"
          title="Sin dato disponible para este filtro"
        >
          Sin dato
        </span>
      );
    }
    return <>{formatter(value)}</>;
  }

  // ── Estados UI tempranos ──
  if (restaurantsLoading) {
    return (
      <Card>
        <Text>Cargando lista de restaurants...</Text>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <Text className="text-red-600">Error: {error.message}</Text>
      </Card>
    );
  }
  if (restaurants.length === 0) {
    return (
      <Card>
        <Title>Sin restaurants</Title>
        <Text>No hay restaurants configurados.</Text>
      </Card>
    );
  }

  return (
    <div className="flex gap-3">
      {/* ── Sidebar compartida (template homologado) + filtro Canal ── */}
      <DashboardSidebar
        restaurants={restaurants}
        selectedRestaurantId={selectedRestaurantId}
        onRestaurantChange={(id, slug) => {
          setSelectedRestaurant(id, slug);
          setChannelFilter("all");
        }}
        months={meses}
        selectedMonth={mesSel}
        onMonthChange={setMesSel}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
      >
        {/* Filtro de canal (viene de comercial_diario_ventas, incluye Panguch/Rappi) */}
        <div>
          <Text className="text-xs mb-1">Canal</Text>
          <Select value={channelFilter} onValueChange={setChannelFilter} className="w-full !min-w-0 !py-1 !text-xs">
            <SelectItem value="all">Todos</SelectItem>
            {canalesConDatos.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </Select>
        </div>
      </DashboardSidebar>

      {/* ── Contenido principal (capturable para export) ─── */}
      <div ref={exportRef} className="flex-1 min-w-0 space-y-3">
        {/* Header del dashboard (dentro del export) */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            {selectedRestaurant ? (
              <DashboardHeader
                title="Dashboard Comercial"
                description="Análisis de ventas, canales y comparativo diario"
                restaurant={selectedRestaurant}
                period={resumen?.mes_actual}
              />
            ) : (
              <Title>Dashboard Comercial</Title>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => handleExport("png")}
              disabled={exporting || !resumen}
              title={!resumen ? "Sin datos para exportar" : "Exportar vista como PNG"}
              variant="secondary"
              icon={() => <span>📷</span>}
            >
              {exporting ? "Exportando..." : "PNG"}
            </Button>
            <Button
              onClick={() => handleExport("pdf")}
              disabled={exporting || !resumen}
              title={!resumen ? "Sin datos para exportar" : "Exportar vista como PDF"}
              variant="primary"
              icon={() => <span>📑</span>}
            >
              {exporting ? "Exportando..." : "PDF"}
            </Button>
          </div>
        </div>

        {/* KPIs principales (fuentes: comparativo_diario_ventas + comercial_diario_ventas) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <Card className="!p-3">
            <Text className="text-xs">Ventas totales</Text>
            <Metric className="!text-xl">{formatCurrency(resumen?.ventas_actual ?? 0)}</Metric>
            <Text className="!text-[10px] mt-1">
              <DeltaPct value={resumen?.var_periodo_previo_pct} /> vs periodo previo
            </Text>
            {resumen?.var_anio_anterior_pct != null && (
              <Text className="!text-[10px]">
                <DeltaPct value={resumen?.var_anio_anterior_pct} /> vs año anterior
              </Text>
            )}
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          <Card className="!p-3">
            <Text className="text-xs">Pedidos promedio</Text>
            <Metric className="!text-xl">{formatNumber(resumen?.pedidos_promedio ?? 0)}</Metric>
            <Text className="!text-[10px] mt-1">
              <DeltaPct value={resumen?.var_pedidos_promedio_pct} /> vs periodo previo
            </Text>
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          <Card className="!p-3">
            <Text className="text-xs">Total Pedidos</Text>
            <Metric className="!text-xl">{formatNumber(resumen?.pedidos_total ?? 0)}</Metric>
            <Text className="!text-[10px] mt-1">
              <DeltaPct value={resumen?.var_pedidos_total_pct} /> vs periodo previo
            </Text>
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          <Card className="!p-3">
            <Text className="text-xs">🧾 Ticket promedio</Text>
            <Metric className="!text-xl">
              {resumen?.ventas_actual != null && resumen?.pedidos_total
                ? formatCurrency(resumen.ventas_actual / resumen.pedidos_total)
                : "Sin datos"}
            </Metric>
            <Text className="!text-[10px] mt-1">venta ÷ nº pedidos</Text>
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          <Card className="!p-3">
            <Text className="text-xs">Comensales</Text>
            <Metric className="!text-xl">{formatNumber(resumen?.pax_total ?? 0)}</Metric>
            <Text className="!text-[10px] mt-1">
              <DeltaPct value={resumen?.var_pax_total_pct} /> vs periodo previo
            </Text>
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          {/* ── Ticket x comensal: oculto (pendiente en spec/backlog) ── */}

          <Card className="!p-3">
            <Text className="text-xs">% Mermas</Text>
            <Metric className="!text-xl">
              {renderKpi(
                insights?.mermas ? insights.mermas.pct * 100 : null,
                (n) => `${n.toFixed(2)}%`
              )}
            </Metric>
            {insights?.mermas && (
              <Text className="!text-[10px] mt-1">
                {formatCurrency(insights.mermas.mermas_amount)} / {formatCurrency(insights.mermas.ventas_amount)}
              </Text>
            )}
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
          <Card className="!p-3">
            <Text className="text-xs">{mesSel ? `Venta acum. ${MESES_CORTO[mesSel.mm - 1] ?? mesSel.mm}` : "Venta acum."}</Text>
            <Metric className="!text-xl">
              {renderKpi(ventaYtd, formatCurrency)}
            </Metric>
            <Text className="!text-[10px] mt-1">acumulado del año</Text>
            {resumen?.mes_actual && (
              <Text className="!text-[10px] mt-1 text-tremor-content">{resumen.mes_actual}</Text>
            )}
          </Card>
        </div>

        {/* Ventas por mes y canal + Mix por canal (recuadros independientes) */}
        {ventasMensuales && ventasMensuales.meses.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Recuadro izquierdo: barras apiladas por mes/canal */}
            <Card className="!p-3">
              <Title className="!text-base">Ventas por mes y canal</Title>
              <Text className="!text-xs">
                Últimos {ventasMensuales.meses.length} meses · desglose por canal
                {channelFilter !== "all" && ` · filtrado: ${channelFilter}`}
              </Text>
              <BarChart
                className="mt-3 h-48"
                data={ventasMensualesData}
                index="mes"
                categories={canalesVisibles}
                colors={coloresPara(canalesVisibles)}
                valueFormatter={formatCurrency}
                yAxisWidth={70}
                stack={true}
              />
            </Card>

            {/* Recuadro derecho: Mix por canal (dona gruesa + leyenda + variación) */}
            {resumen && (
              <Card className="!p-3">
                <Title className="!text-base">Mix por canal</Title>
                <Text className="!text-xs">Distribución de ventas ({resumen.mes_actual})</Text>
                {(() => {
                  const rows = (resumen.mix_canal ?? [])
                    .filter((c) => c.venta > 0)
                    .map((c) => ({
                      name: c.canal,
                      value: c.venta,
                      color: CANAL_COLORS[c.canal] ?? PALETTE.gray,
                    }))
                    .sort((a, b) => b.value - a.value);
                  const total = rows.reduce((s, r) => s + r.value, 0);
                  if (rows.length === 0) {
                    return (
                      <Text className="!text-xs mt-3">Sin datos de canal para este periodo.</Text>
                    );
                  }
                  const donutData = rows.map((r) => ({ name: r.name, value: r.value }));
                  const donutColors = rows.map((r) => r.color);
                  return (
                    <div className="grid grid-cols-2 gap-2 items-center mt-2">
                      <ThickDonut
                        data={donutData}
                        colors={donutColors}
                        centerLabel={formatCompactCurrency(total)}
                        centerSub="total"
                        height={160}
                        innerRadius={58}
                        outerRadius={100}
                      />
                      {/* Leyenda estructurada: color · canal · % · valor */}
                      <div className="space-y-1">
                        {rows.map((r) => {
                          const pct = total > 0 ? (r.value / total) * 100 : 0;
                          return (
                            <div key={r.name} className="flex items-center gap-2 text-xs">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                                style={{ backgroundColor: r.color }}
                              />
                              <span className="flex-1 truncate text-tremor-content-emphasis">
                                {r.name}
                              </span>
                              <span className="tabular-nums font-medium w-12 text-right">
                                {pct.toFixed(1)}%
                              </span>
                              <span className="tabular-nums w-16 text-right text-tremor-content">
                                {formatCompactCurrency(r.value)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Indicador de variación vs año anterior */}
                {resumen.var_anio_anterior_pct != null && resumen.mes_actual && (
                  <div className="mt-2 pt-2 border-t border-tremor-border">
                    <span
                      className={`inline-flex items-center gap-1 text-sm font-semibold ${
                        resumen.var_anio_anterior_pct >= 0
                          ? "text-emerald-600"
                          : "text-red-600"
                      }`}
                    >
                      {resumen.var_anio_anterior_pct >= 0 ? "▲" : "▼"}{" "}
                      {Math.abs(resumen.var_anio_anterior_pct).toFixed(1)}%
                    </span>
                    <span className="text-xs text-tremor-content ml-1">
                      vs. {resumen.mes_actual.split(" ")[0]} {Number(resumen.mes_actual.split(" ")[1]) - 1}
                    </span>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {!resumen ? (
          <Card>
            <Text>Cargando datos de {restaurantName || "…"}...</Text>
          </Card>
        ) : (
          <>
            <div className={`grid grid-cols-1 gap-3 ${diasSemanaData.length > 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
              <Card className="!p-3">
                <Title className="!text-base">Ventas diarias</Title>
                <Text className="!text-xs">
                  {ventasDiariasCmp
                    ? `${ventasDiariasCmp.label_t} vs mes anterior (${ventasDiariasCmp.label_t_menos_1})`
                    : "Serie temporal de ventas (comercial_diario_ventas)"}
                </Text>
                {ventasDiariasCmp && ventasDiariasCmp.dias.length > 0 ? (
                  (() => {
                    const hayT1 = ventasDiariasCmp.dias.some(
                      (d) => d.venta_t_menos_1 != null
                    );
                    const cats = hayT1
                      ? [ventasDiariasCmp.label_t, ventasDiariasCmp.label_t_menos_1]
                      : [ventasDiariasCmp.label_t];
                    const cols = hayT1
                      ? [PALETTE.primaryDark, "#E9560D"]
                      : [PALETTE.primaryDark];
                    return (
                  <LineChart
                    className="mt-3 h-56"
                    data={ventasDiariasMesAntData}
                    index="dd"
                    categories={cats}
                    colors={cols}
                    curveType="monotone"
                    valueFormatter={formatCurrency}
                    yAxisWidth={70}
                    showLegend={true}
                    showAnimation={false}
                    style={{ ["--tr-line-chart-stroke-width" as any]: "1.5px" }}
                  />
                    );
                  })()
                ) : (
                  <AreaChart
                    className="mt-3 h-56"
                    data={ventasDiariasData}
                    index="date"
                    categories={["ventas"]}
                    colors={[PALETTE.primaryDark]}
                    valueFormatter={formatCurrency}
                    yAxisWidth={70}
                    showLegend={false}
                  />
                )}
              </Card>

              <Card className="!p-3">
                <Title className="!text-base">Comparativo diario año anterior</Title>
                <Text className="!text-xs">
                  {ventasDiariasCmp
                    ? `${ventasDiariasCmp.label_t} vs año anterior (mismo mes)`
                    : "Mes t vs año anterior"}
                </Text>
                {ventasDiariasCmp && ventasDiariasCmp.dias.length > 0 ? (
                  (() => {
                    const hayAnioAnt = ventasDiariasCmp.dias.some(
                      (d) => d.venta_anio_anterior != null
                    );
                    const categorias = hayAnioAnt
                      ? [ventasDiariasCmp.label_t, "Año anterior"]
                      : [ventasDiariasCmp.label_t];
                    const colores = hayAnioAnt
                      ? [PALETTE.primaryDark, "#E9560D"]
                      : [PALETTE.primaryDark];
                    return (
                  <LineChart
                    className="mt-3 h-56"
                    data={ventasDiariasCmpData}
                    index="dd"
                    categories={categorias}
                    colors={colores}
                    curveType="monotone"
                    valueFormatter={formatCurrency}
                    yAxisWidth={70}
                    showLegend={true}
                    showAnimation={false}
                    style={{ ["--tr-line-chart-stroke-width" as any]: "1.5px" }}
                  />
                    );
                  })()
                ) : (
                  <Text className="mt-3 text-tremor-content">Sin datos comparativos.</Text>
                )}
              </Card>

              {diasSemanaData.length > 0 && (
                <Card className="!p-3">
                  <Title className="!text-base">Días de la semana</Title>
                  <Text className="!text-xs">Venta promedio por día · % sobre el total</Text>
                  <PctBars
                    data={diasSemanaData}
                    colors={diasSemanaData.map(() => PALETTE.primaryDark)}
                    height={190}
                  />
                </Card>
              )}
            </div>
          </>
        )}

        {/* ── Heatmap Ventas en salón por franja horaria ── */}
        {heatmap && heatmap.celdas.length > 0 && (
          <div>
            {heatmap.demo && (
              <div className="text-[11px] text-amber-600 mb-1">
                ⚠ Datos de demostración.
              </div>
            )}
            <HeatmapFranjaHoraria data={heatmap.celdas} />
          </div>
        )}

        {/* ── Ventas por Producto (BI section) ──
            Panel de análisis de producto desde ventas_mensuales_producto.
            Usa el restaurante seleccionado en el sidebar. */}
        <VentasPorProductoSection
          restaurantId={selectedRestaurantId}
          mesSel={mesSel}
        />

        {/* ── Insights de Producto: oculto (pendiente en spec/backlog) ── */}

        {/* ── Insights del tablero Comercial (Top zonas, al final) ── */}
        {insights && insights.top_zones.some(z => z.total_sales > 0) && (
          <Card className="!p-3">
            <div className="flex justify-between items-baseline">
              <Title className="!text-base">Top zonas del restaurante</Title>
              <Text className="!text-xs">Ventas por zona (B1-B4)</Text>
            </div>
            <BarList
              className="mt-2"
              data={insights.top_zones
                .filter(z => z.total_sales > 0)
                .map(z => ({ name: `${z.zone} (${z.rotation_count} rotaciones)`, value: z.total_sales }))
              }
              valueFormatter={formatCurrency}
              color="emerald"
            />
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {insights.top_zones.map(z => (
                <div key={z.zone} className="text-xs border-r last:border-r-0 pr-2">
                  <Text className="!text-[10px] uppercase text-tremor-content">{z.zone}</Text>
                  <Text className="!text-sm font-semibold">
                    {z.avg_ticket > 0 ? formatCurrency(z.avg_ticket) : "—"}
                  </Text>
                  <Text className="!text-[10px] text-tremor-content">ticket prom/mesa</Text>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
